import React, { useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, styled } from '@mui/material';

import { useTagAssignmentsForForm } from '../hooks/useTagAssignmentsForForm';

import TagAssignmentsDestination from './TagAssignmentsDestination/TagAssignmentsDestination';
import TagAssignmentsExperience from './TagAssignmentsExperience/TagAssignmentsExperience';
import TagAssignmentsHotel from './TagAssignmentsHotel/TagAssignmentsHotel';

export type EntityType = 'HOTEL' | 'EXPERIENCE' | 'DESTINATION';

export interface AssignmentFormData {
  entityType: EntityType;
  entityId: string;
}

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionStyled = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  tagId?: string;
  assignments: Array<AssignmentFormData>;
  onAssignmentsChange: (assignments: Array<AssignmentFormData>) => void;
}

export default function TagAssignments({ tagId, assignments, onAssignmentsChange }: Props) {
  const [expanded, setExpanded] = useState<Array<string>>([]);
  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const savedAssignmentsPersisted = tagAssignmentsData.savedAssignments;

  useEffect(() => {
    if (tagId && savedAssignments.length > 0 && assignments.length === 0) {
      onAssignmentsChange(savedAssignments);
    }
  }, [tagId, savedAssignments, assignments.length, onAssignmentsChange]);

  const assignmentsByType = useMemo(() => {
    const grouped = {
      HOTEL: assignments.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId),
      EXPERIENCE: assignments.filter((a) => a.entityType === 'EXPERIENCE').map((a) => a.entityId),
      DESTINATION: assignments.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId),
    };
    return grouped;
  }, [assignments]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded((prev) => {
      if (isExpanded) {
        return [...prev, panel];
      }
      return prev.filter((p) => p !== panel);
    });
  };

  const handleHotelChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'HOTEL');
    const hotelAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'HOTEL',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...hotelAssignments]);
  };

  const handleExperienceChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'EXPERIENCE');
    const experienceAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'EXPERIENCE',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...experienceAssignments]);
  };

  const handleDestinationChange = (selectedPlaceIds: Array<string>) => {
    const savedDestinationIds = tagId
      ? savedAssignmentsPersisted.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId)
      : [];

    const allDestinationIds = [
      ...savedDestinationIds,
      ...selectedPlaceIds.filter((id) => !savedDestinationIds.includes(id)),
    ];

    const otherAssignments = assignments.filter((a) => a.entityType !== 'DESTINATION');
    const destinationAssignments: Array<AssignmentFormData> = allDestinationIds.map((placeId) => ({
      entityType: 'DESTINATION',
      entityId: placeId,
    }));
    onAssignmentsChange([...otherAssignments, ...destinationAssignments]);
  };

  return (
    <Box>
      <AccordionStyled expanded={expanded.includes('destination')} onChange={handleAccordionChange('destination')}>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Destination Assignments</Typography>
            {assignmentsByType.DESTINATION.length > 0 && (
              <Typography variant="body2" ml={2} color="text.secondary">
                ({assignmentsByType.DESTINATION.length} selected)
              </Typography>
            )}
          </Box>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          <TagAssignmentsDestination
            tagId={tagId}
            selectedPlaceIds={assignmentsByType.DESTINATION}
            onSelectionChange={handleDestinationChange}
          />
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled expanded={expanded.includes('hotel')} onChange={handleAccordionChange('hotel')}>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Hotel Assignments</Typography>
            {assignmentsByType.HOTEL.length > 0 && (
              <Typography variant="body2" ml={2} color="text.secondary">
                ({assignmentsByType.HOTEL.length} selected)
              </Typography>
            )}
          </Box>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          <TagAssignmentsHotel
            tagId={tagId}
            selectedOfferIds={assignmentsByType.HOTEL}
            onSelectionChange={handleHotelChange}
            savedOfferIds={savedAssignmentsPersisted.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId)}
          />
        </AccordionDetailsStyled>
      </AccordionStyled>
      <AccordionStyled expanded={expanded.includes('experience')} onChange={handleAccordionChange('experience')}>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Experience Assignments</Typography>
            {assignmentsByType.EXPERIENCE.length > 0 && (
              <Typography variant="body2" ml={2} color="text.secondary">
                ({assignmentsByType.EXPERIENCE.length} selected)
              </Typography>
            )}
          </Box>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          <TagAssignmentsExperience
            tagId={tagId}
            selectedOfferIds={assignmentsByType.EXPERIENCE}
            onSelectionChange={handleExperienceChange}
            savedOfferIds={savedAssignmentsPersisted
              .filter((a) => a.entityType === 'EXPERIENCE')
              .map((a) => a.entityId)}
          />
        </AccordionDetailsStyled>
      </AccordionStyled>
    </Box>
  );
}
